
export type LessonAction = 'canceled' | 'completed' | 'no-show' | 'reschedule' | 'details' | 'confirmed';
export type UserRole = undefined | 'admin' | 'teacher' | 'parent' | 'student';
export type AccountStatus = "active" | "suspended" | "deleted" | "paused" | "inactive";
export type NotificationType = 'success'|'money'|'urgent';

export type NavItem = {
  path: string | undefined;
  title: string;
  icon: React.ReactNode;
  items?: NavItem[];  // 👈 New: subitems
};

export interface SignupFormField {
  id: string;
  label: string;
  type: 'text' | 'tel' | 'email' | 'textarea';
  required: boolean;
  default: boolean;
}

export interface SignupForm {
  id: string;
  name: string;
  fields: SignupFormField[];
  created_at: string;
  student_count: number;
}

export interface Lesson {
    id: string;
    time: string;        // new Date() string
    duration: string;    // "45min"
    student: string;     // "Emma Dupont"
    student_id: string;     // "Emma Dupont"
    teacher: string;     // "Teacher Dupont"
    teacher_id: string;     // "Teacher Dupont"
    status: LessonAction;
    location: string;    // "Online" | "Studio Antibes"
    notes?: string;
    student_rating: number;
    student_feedback: string;
    teacher_feedback: string;
    teacher_rating: number;
    feedback_at: Date;
    start_time?: Date; // calculated from time
    end_time?: Date;   // calculated from time + duration
}

export interface Notification {
    id: string;
    user_id: string;
    type: 'success' | 'money' | 'urgent';
    title: string;
    short_text: string;
    destination: string;
    created_at: string;
    expires_at: string;
    seen: boolean;
}

export interface AvailableSlot {
    start: Date; // ISO date string
    end: Date;   // ISO date string
}

interface TeacherSettings {
  sessionLengthMinutes: number;
  workingDays: string[];
  workingHours: Record<string, { start: string; end: string }>;
  breaks: Array<{ id: string; start: string; end: string }>;
}

export interface Teacher {
    id: string;
    auth_id: string;
    email:  string;
    phone: string;
    price_per_h: number;
    first_name: string;
    last_name: string;
    picture: string;
    auth_provider: string;
    status: AccountStatus;
    description: string;
    expiration_date: Date;
    notes: string;
    teacher_settings: TeacherSettings;
}

export interface Parent {
    id: string;
    auth_id: string;
    email:  string;
    phone: string;
    first_name: string;
    last_name: string;
    picture: string;
    auth_provider: string;
    teacher_id: string[];
    student_id: string[];
    status: AccountStatus;
    description: string;
    notes: string;
}

export const routes_per_type: Record<string, string> = {
    student: `/students`,
    parent: `/parents`,
    teacher: `/teachers`,
  } as const;

export interface Student {
    id: string;
    auth_id: string;
    first_name: string;
    last_name: string;
    picture: string;
    auth_provider: string;
    email: string;
    balance: number;
    status: AccountStatus;
    phone?: string;
    teacher_id: string[]; // uuid of assigned teacher
    notes?: string;
    parentEmail?: string;
    parentPhone?: string;
}

export interface SupabaseState {
    students: Student[];
    teachers: Teacher[];
    parents: Parent[];
    userProfile: Student | Parent | Teacher;
    availableSlots: AvailableSlot[];
    allTimeslotswOfTheDay: AvailableSlot[];
    lessons: Lesson[];
    userRole: UserRole | undefined;
    notifications: Notification[];
    isOnline: boolean;
    isSyncing: boolean;
    lastSync: string | null;
    ledger?: LedgerEntry[];
}

export interface LedgerEntry {
    id: string;
    student_id: string;
    teacher_id: string;
    date: string;
    label: string;
    amount: number;
    description: string;
    created_at: string;
}


const base = {
    id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    notes: '',
    status: 'active' as const,
    auth_id: '',
    auth_provider: '',
    picture: '',
};

export const baseStudent = {
    ...base,
    balance: 0,
    teacher_id: [],
}

export const baseParent = {
    ...base,
    description: '',
    student_id: [],
    teacher_id: [],
}

export const baseTeacher = {
    ...base,
    description: '',
    price_per_h: 0,
    expiration_date: new Date(),
    teacher_settings: {
        sessionLengthMinutes: 60,
        workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], // M-F default
        workingHours: {
            monday:   { start: '09:00', end: '18:00' },
            tuesday:  { start: '09:00', end: '18:00' },
            wednesday: { start: '09:00', end: '18:00' },
            thursday: { start: '09:00', end: '18:00' },
            friday:   { start: '09:00', end: '17:00' },
            saturday: { start: '10:00', end: '16:00' },
            sunday:   { start: '00:00', end: '00:00' } // disabled
        },
        breaks: [{ id: 'default', start: '12:00', end: '13:00' }]
    },
}
